/**
 * FP3-125B — Read-only "Why stale?" diagnostic drawer.
 *
 * Opened from Today → Needs Attention. Fetches the exact same
 * data the freshness resolver considered for this student — one
 * row per source document, with source collection, document ID,
 * session_id, raw + resolved dates, chartable/superseded flags,
 * and explicit exclusion reasons. Also surfaces `unmatched_
 * student_evidence`: rows that DO exist in the student's evidence
 * universe but are tied to inactive/replaced goals, and therefore
 * do NOT lift freshness for any current active goal.
 *
 * Strict READ-ONLY. No mutation UI. Provider auth is enforced
 * server-side (own caseload · admin same-org · cross-org → 403).
 *
 * Design goal: a provider (Sarah/Peter) can click "Why stale?"
 * for William Frates and get an answer without touching Mongo:
 *
 *      Goal A · r-sentence
 *         last_data_date: Aug 14 · days_since: 2 · stale: false
 *         Rows considered:
 *           trial_runs · tr-abc123 · raw 2026-07-15 → resolved 2026-08-14
 *              session: sess-xyz789 · 5/10 correct
 *
 *      Other evidence (not counted — tied to inactive goals):
 *         goal_evidence · ev-def456 · Aug 14 · goal_id "old-r-goal"
 *            reason: inactive_goal (status=archived)
 */
import { useEffect, useMemo, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { CircleNotch, WarningCircle, CheckCircle, Info } from "@phosphor-icons/react";
import api from "@/lib/api";

const REASON_LABEL = {
  superseded: "Superseded (FP3-122 correction)",
  narrative_only: "Narrative-only (no chartable data)",
  chartable_false: "chartable=false",
  chartable_missing: "chartable field missing (legacy)",
  inactive_goal: "Tied to inactive/archived goal",
  goal_id_not_found: "Tied to a goal that no longer exists",
  goal_id_missing: "Row has no goal_id",
  other: "Other exclusion",
};

function reasonPill(reason) {
  const label = REASON_LABEL[reason] || reason;
  return (
    <span
      data-testid={`exclusion-reason-${reason}`}
      className="text-[10px] px-2 py-0.5 rounded-full bg-[#FDF2F2] text-[#B85C5C] border border-[#B85C5C]/20 whitespace-nowrap"
    >
      {label}
    </span>
  );
}

function DateCell({ raw, resolved }) {
  const drift = raw && resolved && raw !== resolved;
  return (
    <span className="tabular-nums whitespace-nowrap">
      {resolved || raw || "—"}
      {drift && (
        <span
          className="ml-1 text-[10px] text-[#9C6B2E]"
          title={`raw stored date: ${raw}`}
        >
          (raw {raw})
        </span>
      )}
    </span>
  );
}

export function WhyStaleDrawer({ studentId, studentName, open, onOpenChange }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!open || !studentId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .getFreshnessDebug(studentId)
      .then((r) => {
        if (!cancelled) setData(r.data);
      })
      .catch((e) => {
        if (!cancelled) {
          setError(
            e?.response?.data?.detail || e?.message || "Failed to load diagnostic",
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, studentId]);

  const staleCount = data?.stale_goals_count ?? 0;
  const activeCount = data?.active_goals_count ?? 0;
  const unmatched = useMemo(
    () => (data?.unmatched_student_evidence || []).sort(
      (a, b) => (b.resolved_date || "").localeCompare(a.resolved_date || ""),
    ),
    [data],
  );

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        data-testid="why-stale-drawer"
        className="w-full sm:max-w-[640px] overflow-y-auto"
      >
        <SheetHeader>
          <SheetTitle data-testid="why-stale-title" className="heading-font">
            Why stale? · {studentName}
          </SheetTitle>
          <SheetDescription className="text-xs text-[#6B7280]">
            Read-only diagnostic — every row the freshness resolver considered,
            excluded, or found tied to an inactive goal. No writes.
          </SheetDescription>
        </SheetHeader>

        {loading && (
          <div className="flex items-center gap-2 mt-6 text-sm text-[#6B7280]" data-testid="why-stale-loading">
            <CircleNotch size={16} className="animate-spin" /> Loading diagnostic…
          </div>
        )}

        {error && (
          <div
            className="mt-6 p-3 rounded-lg bg-[#FDF2F2] text-[#B85C5C] text-sm border border-[#B85C5C]/20"
            data-testid="why-stale-error"
          >
            {error}
          </div>
        )}

        {data && !loading && !error && (
          <div className="mt-4 space-y-5">
            {/* Header summary */}
            <div className="p-3 rounded-lg bg-[#FDF8F3] border border-[#E9DAC0]">
              <div className="flex items-center gap-2 text-sm text-[#1F2937]">
                <Info size={16} weight="duotone" className="text-[#9C6B2E]" />
                <span data-testid="why-stale-summary">
                  <strong>{staleCount}</strong> of {activeCount} active goal
                  {activeCount === 1 ? "" : "s"} flagged stale · today {data.today} · cutoff {data.stale_cutoff}
                </span>
              </div>
            </div>

            {/* Per-active-goal detail */}
            {(data.goals || []).map((g) => (
              <div
                key={g.goal_id}
                data-testid={`why-stale-goal-${g.goal_id}`}
                data-stale={g.stale ? "true" : "false"}
                className="border border-[#E5E3DB] rounded-xl overflow-hidden"
              >
                <div className={`p-3 ${g.stale ? "bg-[#FDF2F2]" : "bg-[#EFF3F1]"}`}>
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-sm font-medium text-[#1F2937] truncate">
                      {g.goal_text || <span className="italic text-[#6B7280]">(no goal text)</span>}
                    </p>
                    <Badge
                      data-testid={`why-stale-badge-${g.goal_id}`}
                      className={
                        g.stale
                          ? "bg-[#B85C5C]/10 text-[#B85C5C] border-[#B85C5C]/30"
                          : "bg-[#3F7A5C]/10 text-[#3F7A5C] border-[#3F7A5C]/30"
                      }
                    >
                      {g.stale ? "Stale" : "Fresh"}
                    </Badge>
                  </div>
                  <div className="text-[11px] text-[#6B7280] mt-1 flex items-center gap-3 flex-wrap tabular-nums">
                    <span>goal_id: <span className="font-mono">{g.goal_id.slice(0, 12)}…</span></span>
                    <span>last data: {g.last_data_date || "never"}</span>
                    {g.days_since != null && <span>{g.days_since} days ago</span>}
                  </div>
                </div>

                {/* Rows considered */}
                <div className="p-3 space-y-2">
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-[#6B7280]">
                    Rows considered ({(g.rows_considered || []).length})
                  </p>
                  {(g.rows_considered || []).length === 0 ? (
                    <p className="text-xs text-[#6B7280] italic" data-testid={`why-stale-empty-considered-${g.goal_id}`}>
                      No active chartable rows attached to this goal.
                    </p>
                  ) : (
                    <div className="space-y-1">
                      {g.rows_considered.map((r) => (
                        <div
                          key={`${r.collection}-${r.id}`}
                          data-testid={`considered-row-${r.id}`}
                          className="text-xs px-2 py-1.5 rounded-md bg-[#FBFAF6] border border-[#E5E3DB] flex items-center justify-between gap-2"
                        >
                          <div className="flex-1 min-w-0">
                            <span className="font-mono text-[#4A6B8C]">{r.collection}</span>
                            <span className="text-[#6B7280] mx-1">·</span>
                            <span className="font-mono text-[#1F2937] truncate">{(r.id || "").slice(0, 12)}…</span>
                            {r.session_id && (
                              <>
                                <span className="text-[#6B7280] mx-1">·</span>
                                <span className="text-[10px] text-[#6B7280]">
                                  session <span className="font-mono">{r.session_id.slice(0, 8)}…</span>
                                </span>
                              </>
                            )}
                            {(r.correct != null && r.trials != null) && (
                              <span className="text-[10px] text-[#6B7280] ml-2">
                                {r.correct}/{r.trials}
                              </span>
                            )}
                          </div>
                          <DateCell raw={r.raw_date} resolved={r.resolved_date} />
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Rows excluded */}
                  {(g.rows_excluded || []).length > 0 && (
                    <>
                      <p className="text-[11px] font-semibold uppercase tracking-wider text-[#B85C5C] mt-3">
                        Rows excluded ({g.rows_excluded.length})
                      </p>
                      <div className="space-y-1">
                        {g.rows_excluded.map((r) => (
                          <div
                            key={`${r.collection}-${r.id}`}
                            data-testid={`excluded-row-${r.id}`}
                            className="text-xs px-2 py-1.5 rounded-md bg-[#FDF2F2] border border-[#B85C5C]/20 flex items-center justify-between gap-2"
                          >
                            <div className="flex-1 min-w-0">
                              <span className="font-mono text-[#4A6B8C]">{r.collection}</span>
                              <span className="text-[#6B7280] mx-1">·</span>
                              <span className="font-mono text-[#1F2937]">{(r.id || "").slice(0, 12)}…</span>
                              <span className="text-[10px] text-[#6B7280] ml-2">{r.raw_date || "no date"}</span>
                            </div>
                            {reasonPill(r.reason)}
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </div>
            ))}

            {/* Cross-cutting: unmatched evidence tied to inactive goals */}
            {unmatched.length > 0 && (
              <div className="border border-[#B85C5C]/30 rounded-xl overflow-hidden" data-testid="why-stale-unmatched">
                <div className="p-3 bg-[#FDF2F2]">
                  <div className="flex items-center gap-2">
                    <WarningCircle size={16} weight="duotone" className="text-[#B85C5C]" />
                    <p className="text-sm font-medium text-[#1F2937]">
                      Other evidence for this student ({unmatched.length}) —
                      <span className="text-[#B85C5C]"> not counted</span>
                    </p>
                  </div>
                  <p className="text-[11px] text-[#6B7280] mt-1">
                    These rows exist in this student&apos;s evidence universe but are
                    tied to goal_ids that are NOT in the active goal set. If any
                    of these dates are recent, the goal may have been replaced or
                    archived — Data tab still shows them via student.goals lookup.
                  </p>
                </div>
                <div className="p-3 space-y-1">
                  {unmatched.slice(0, 40).map((r) => (
                    <div
                      key={`${r.collection}-${r.id}`}
                      data-testid={`unmatched-row-${r.id}`}
                      className="text-xs px-2 py-1.5 rounded-md bg-[#FBFAF6] border border-[#E5E3DB]"
                    >
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <div className="flex-1 min-w-0">
                          <span className="font-mono text-[#4A6B8C]">{r.collection}</span>
                          <span className="text-[#6B7280] mx-1">·</span>
                          <span className="font-mono text-[#1F2937]">{(r.id || "").slice(0, 12)}…</span>
                          {(r.correct != null && r.trials != null) && (
                            <span className="text-[10px] text-[#6B7280] ml-2">{r.correct}/{r.trials}</span>
                          )}
                        </div>
                        <DateCell raw={r.raw_date} resolved={r.resolved_date} />
                        {reasonPill(r.reason)}
                      </div>
                      <div className="text-[10px] text-[#6B7280] mt-1 flex items-center gap-2 flex-wrap">
                        <span>goal_id: <span className="font-mono">{(r.goal_id || "—").slice(0, 12)}…</span></span>
                        <span>goal_status: <span className="font-mono">{r.goal_status || "MISSING"}</span></span>
                        {r.goal_text && <span className="truncate">&ldquo;{r.goal_text}&rdquo;</span>}
                      </div>
                    </div>
                  ))}
                  {unmatched.length > 40 && (
                    <p className="text-[11px] text-[#6B7280] italic pt-1">
                      Showing 40 of {unmatched.length} unmatched rows.
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Ancillary — all goals list (to spot archived/replaced) */}
            {(data.all_student_goals || []).length > (data.goals || []).length && (
              <details className="border border-[#E5E3DB] rounded-xl overflow-hidden" data-testid="why-stale-all-goals">
                <summary className="p-3 cursor-pointer text-sm text-[#1F2937] hover:bg-[#FBFAF6]">
                  All goals on record ({data.all_student_goals.length})
                </summary>
                <div className="p-3 space-y-1">
                  {data.all_student_goals.map((g) => (
                    <div
                      key={g.id}
                      className="text-xs px-2 py-1 rounded-md bg-[#FBFAF6] border border-[#E5E3DB] flex items-center gap-2"
                    >
                      <span
                        className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                          g.status === "active"
                            ? "bg-[#EFF3F1] text-[#3F7A5C] border border-[#3F7A5C]/30"
                            : "bg-[#F3F1EC] text-[#6B7280] border border-[#E5E3DB]"
                        }`}
                      >
                        {g.status || "—"}
                      </span>
                      <span className="font-mono text-[10px] text-[#6B7280]">
                        {g.id.slice(0, 10)}…
                      </span>
                      <span className="text-xs text-[#1F2937] truncate">{g.goal_text || "(no text)"}</span>
                    </div>
                  ))}
                </div>
              </details>
            )}

            {staleCount === 0 && (
              <div
                className="p-3 rounded-lg bg-[#EFF3F1] border border-[#D6E4DA] text-sm text-[#3F7A5C] flex items-center gap-2"
                data-testid="why-stale-none"
              >
                <CheckCircle size={16} weight="duotone" />
                No active goals are currently stale.
              </div>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

export default WhyStaleDrawer;
